import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreateFiservPaymentSettingsDocument,
    type CreateFiservPaymentSettingsMutation,
    type CreateFiservPaymentSettingsMutationVariables,
} from '../../../../api/mutations/createFiservPaymentSettings';
import { type FiservPaymentSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type AddFiservPaymentSettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = FiservPaymentSettingsInput;

const initialValues: FormValues = {
    amount: 0,
    secretKey: '',
    currency: '',
    displayName: '',
    storeName: '',
    paymentEndpoint: '',
};

const AddFiservPaymentSettingsForm = ({ moduleId, onComplete }: AddFiservPaymentSettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'fiservPaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('fiservPaymentModuleDetails:paymentSettingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateFiservPaymentSettingsMutation, CreateFiservPaymentSettingsMutationVariables>({
                    mutation: CreateFiservPaymentSettingsDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('fiservPaymentModuleDetails:paymentSettingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator());

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addFiservPaymentSettings"
                    layout="vertical"
                    name="addFiservPaymentSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('fiservPaymentModuleDetails:dividers.mainDetails')}</Divider>
                    <InputField
                        {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                            returnObjects: true,
                        })}
                        name="currency"
                    />
                    <InputNumberField
                        {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                            returnObjects: true,
                        })}
                        name="amount"
                    />
                    <Divider>{t('fiservPaymentModuleDetails:dividers.paymentSettings')}</Divider>
                    <InputField
                        {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.storeName', {
                            returnObjects: true,
                        })}
                        name="storeName"
                    />
                    <PasswordField
                        {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.secretKey', {
                            returnObjects: true,
                        })}
                        name="secretKey"
                    />
                    <InputField
                        {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.paymentEndpoint', {
                            returnObjects: true,
                        })}
                        name="paymentEndpoint"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddFiservPaymentSettingsForm;
